import type { Product } from "../productCommons/index.js";

export type OrderItem = {
  price: number;
  quantity: number;
  product: Product;
};

/**
 * Rounded VAT for one net line in minor units, including its quantity.
 * For an order use calculateVatTotal; aggregating lines first changes rounding.
 */
export const calculateVat = (preVatTotal: number, vatPercentage: number) => {
  if (vatPercentage < 0)
    throw new Error("Vat percentage cannot be less than 0");
  if (vatPercentage > 100)
    throw new Error("Vat percentage cannot be greater than 100");
  return Math.round(preVatTotal * (vatPercentage / 100));
};

/** Sum independently rounded VAT for items, delivery and a negative discount line. */
export const calculateVatTotal = (params: {
  orderItems: Pick<OrderItem, "price" | "quantity">[];
  deliveryCharge: number;
  discountAmount: number;
  vatPercentage: number;
}): number => {
  const vatTotal = params.orderItems
    .concat([
      { price: params.deliveryCharge, quantity: 1 },
      { price: -params.discountAmount, quantity: 1 },
    ])
    .map((orderItem) =>
      calculateVat(orderItem.price * orderItem.quantity, params.vatPercentage),
    )
    .reduce((acc, lineItemTotal) => acc + lineItemTotal, 0);

  return vatTotal < 0 ? 0 : vatTotal;
};

/** One line in minor units, with VAT rounded after multiplying price by quantity. */
export const calculateLineItemTotal = (
  item: { price: number; quantity: number },
  vatPercentage: number,
): number => {
  const vat = calculateVat(item.price * item.quantity, vatPercentage);

  return item.price * item.quantity + vat;
};

/** Net item subtotal in minor units. Retain the original items for VAT calculation. */
export const calculateItemTotal = (
  items?: { price: number; quantity: number }[],
): number => {
  if (!items) return 0;
  return items
    .map((item) => item.price * item.quantity)
    .reduce((accumulator, value) => accumulator + value, 0);
};

/** Net order subtotal, clamped to zero. This aggregate is not an input to calculateVat. */
export const calculatePreVatTotal = (params: {
  orderItems: { price: number; quantity: number }[];
  deliveryCharge: number;
  discountAmount: number;
}): number => {
  const orderItemTotal = calculateItemTotal(params.orderItems);

  const preVatTotal =
    orderItemTotal + params.deliveryCharge - params.discountAmount;

  if (preVatTotal <= 0) return 0;

  return preVatTotal;
};
